import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function read(params, key, fallback) {
  if (Array.isArray(fallback)) return params.has(key) ? params.getAll(key) : fallback;
  if (fallback && typeof fallback === "object") {
    return Object.fromEntries(Object.entries(fallback).map(([field, value]) => [field, read(params, `${key}.${field}`, value)]));
  }
  if (typeof fallback === "boolean") return params.has(key) ? params.get(key) === "1" : fallback;
  return params.get(key) ?? fallback;
}

function write(params, key, value, fallback) {
  if (fallback && !Array.isArray(fallback) && typeof fallback === "object") {
    Object.entries(fallback).forEach(([field, initial]) => write(params, `${key}.${field}`, value[field], initial));
    return;
  }
  params.delete(key);
  if (JSON.stringify(value) === JSON.stringify(fallback) || value == null || value === "") return;
  if (Array.isArray(value)) value.forEach((item) => params.append(key, item));
  else params.set(key, typeof value === "boolean" ? (value ? "1" : "0") : value);
}

// The URL is the source of truth, including refresh and browser Back/Forward.
export default function useUrlSearchState(key, initialValue) {
  const location = useLocation();
  const navigate = useNavigate();
  const defaults = JSON.stringify(initialValue);
  const value = useMemo(() => read(new URLSearchParams(location.search), key, JSON.parse(defaults)), [location.search, key, defaults]);
  const setValue = useCallback((next) => {
    const params = new URLSearchParams(location.search);
    const fallback = JSON.parse(defaults);
    const current = read(params, key, fallback);
    const updated = typeof next === "function" ? next(current) : next;
    if (JSON.stringify(current) === JSON.stringify(updated)) return;
    write(params, key, updated, fallback);
    const search = params.toString();
    if (search === location.search.replace(/^\?/, "")) return;
    navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state, preventScrollReset: true });
  }, [defaults, key, location.hash, location.pathname, location.search, location.state, navigate]);
  return [value, setValue];
}
